import { SubscriptionPlanId, YachtItem, BlogPost, MediaAsset, YachtRoute, YachtMarina } from '../types';

export const APP_NAME = 'Adiosoft';
export const APP_SLOGAN = 'Profesyonel Multi-Tenant SaaS Website Builder';

export interface PlanConfig {
  id: SubscriptionPlanId;
  name: string;
  industry: 'CORPORATE' | 'YACHT_RENTAL';
  price: number;
  priceTRY: number;
  currency: 'TRY';
  billingPeriod: 'Yıllık' | 'Aylık';
  description: string;
  storageGb: number;
  websiteLimit?: number;
  maxSites?: number;
  isWebsiteSubscription?: boolean;
  features: string[];
  recommended?: boolean;
}

const CORP_PLAN: PlanConfig = {
  id: 'PLAN_CORP_ANNUAL',
  name: 'Kurumsal Web Sitesi Paketi',
  industry: 'CORPORATE',
  price: 15000,
  priceTRY: 15000,
  currency: 'TRY',
  billingPeriod: 'Yıllık',
  description: "Şirketler, holdingler, danışmanlık firmaları ve KOBİ'ler için tam kapsamlı kurumsal website ve yönetim paketi.",
  storageGb: 10,
  websiteLimit: 1,
  maxSites: 1,
  isWebsiteSubscription: true,
  features: [
    'Website Hakkı: 1 Web Sitesi (Maksimum 1 Site/Domain)',
    'AI Destekli Web Sitesi Oluşturucu & Görsel Editör',
    'Sınırsız Sayfa & Menü Mimarisi',
    'Kurumsal Blog, Hizmet & İletişim Modülleri',
    'Özel Domain Bağlama & Ücretsiz SSL',
    '10 GB Bulut Depolama Alanı',
    'Gelişmiş Yerel SEO & Schema.org Altyapısı',
    'Multi-tenant Güvenli Bulut Mimarisi',
    'Form & İletişim Talebi Yönetimi',
    '7/24 Kesintisiz Yüksek Hızlı Bulut Hosting',
  ],
};

const YACHT_PLAN: PlanConfig = {
  id: 'PLAN_YACHT_ANNUAL',
  name: 'Lüks Yat Kiralama Paketi',
  industry: 'YACHT_RENTAL',
  price: 36000,
  priceTRY: 36000,
  currency: 'TRY',
  billingPeriod: 'Yıllık',
  description: 'Yat kiralama acenteleri, tekne sahipleri ve mavi tur operatörleri için rezervasyon motorlu profesyonel SaaS çözümü.',
  storageGb: 25,
  websiteLimit: 1,
  maxSites: 1,
  isWebsiteSubscription: true,
  recommended: true,
  features: [
    'Website Hakkı: 1 Yat Kiralama Web Sitesi (Maksimum 1 Site/Domain)',
    'Görsel Drag & Drop Sayfa Editörü',
    '4 Adet Ultra-Lüks Yatçılık Teması',
    'Gelişmiş Yat Filosu Yönetimi (Kabin, Boy, Lokasyon, Fiyat)',
    'Frontend Rezervasyon Talep Motoru',
    'Dashboard Rezervasyon Takip & Detay Paneli',
    'İnteraktif Müsaitlik & Doluluk Takvimi',
    'Yat Filtreleme & Arama Bileşenleri',
    'Özel Domain Bağlama & Ücretsiz SSL',
    '25 GB Yüksek Çözünürlüklü Medya Depolama',
    'Sektörel Blog & SEO Altyapısı',
    '7/24 VIP Hosting Altyapısı',
  ],
};

export const SUBSCRIPTION_PLANS: Record<SubscriptionPlanId, PlanConfig> = {
  PLAN_CORP_ANNUAL: CORP_PLAN,
  PLAN_YACHT_ANNUAL: YACHT_PLAN,
  PLAN_CORPORATE: { ...CORP_PLAN, id: 'PLAN_CORPORATE' },
  PLAN_YACHT: { ...YACHT_PLAN, id: 'PLAN_YACHT' },
  CORPORATE_WEBSITE: { ...CORP_PLAN, id: 'CORPORATE_WEBSITE' },
  YACHT_RENTAL_WEBSITE: { ...YACHT_PLAN, id: 'YACHT_RENTAL_WEBSITE' },
  PLAN_EXTERNAL_HOSTING_LICENSE: {
    id: 'PLAN_EXTERNAL_HOSTING_LICENSE',
    name: 'Lisans ve Altyapı İşlemleri',
    industry: 'CORPORATE',
    price: 3000,
    priceTRY: 3000,
    currency: 'TRY',
    billingPeriod: 'Yıllık',
    storageGb: 0,
    websiteLimit: 1,
    maxSites: 1,
    isWebsiteSubscription: true,
    description: 'Sitenizi Adiosoft altyapısına bağlayın. Kendi GoDaddy, cPanel veya özel hostinginizde 1 yıl kesintisiz ve güvenli yayın hakkı.',
    features: [
      'Yıllık Lisans ve Altyapı Bağlantısı',
      'cPanel / GoDaddy / Nginx / Apache Uyumluluğu',
      'Yönetim Ekibi Tarafından Arka Plan Kurulumu',
      '7/24 Kesintisiz Lisans Doğrulama',
      '1 Yıl Geçerli Kullanım Süresi',
    ],
  },
  PLAN_AI_VISIBILITY_ANNUAL: {
    id: 'PLAN_AI_VISIBILITY_ANNUAL',
    name: 'AI Arama Görünürlüğü',
    industry: 'CORPORATE',
    price: 5000,
    priceTRY: 5000,
    currency: 'TRY',
    billingPeriod: 'Yıllık',
    storageGb: 0,
    websiteLimit: 0,
    maxSites: 0,
    isWebsiteSubscription: false,
    description: 'ChatGPT Search, Gemini ve modern yapay zeka arama motorlarında markanızın ve işletmenizin doğru, tutarlı web verileriyle bulunabilirliğini artıran teknik SEO ve varlık optimizasyonu.',
    features: [
      'AI Arama Motorları İçin Yapılandırılmış Veri (JSON-LD)',
      'Marka & İşletme Varlık Bilgisi Standardizasyonu',
      'Otomatik Sitemap.xml & Robots.txt Senkronizasyonu',
      'Arama & Grounding Uyumlu İşletme Bilgi Sayfası',
      'Entity Tutarlılık ve Bilgi Bütünlüğü Denetimi',
      'Teknik SEO & Canonical Doğrulama Takibi',
      'Yıllık Kesintisiz AI Görünürlük Altyapısı',
    ],
  },
  AI_LOCAL_VISIBILITY: {
    id: 'AI_LOCAL_VISIBILITY',
    name: 'AI Yerel İşletme Görünürlüğü',
    industry: 'CORPORATE',
    price: 5000,
    priceTRY: 5000,
    currency: 'TRY',
    billingPeriod: 'Yıllık',
    storageGb: 0,
    websiteLimit: 0,
    maxSites: 0,
    isWebsiteSubscription: false,
    description: 'BİR GERÇEK İŞLETME / MEKAN / MARKA ENTITY\'SİNİN AI + SEARCH + LOCAL DISCOVERY SİSTEMLERİNDE BULUNABİLİRLİĞİNİ ARTIRMAK VE GERÇEK ARAMA TESTLERİYLE DOĞRULAMAK İÇİN ÖZEL PAKET.',
    features: [
      'Maksimum 1 Aktif İşletme / Mekan Varlığı',
      'Google Arama Grounding ve Gemini Keşif Testleri',
      'OpenAI ve Yerel Arama Sinyalleri Doğrulama',
      'Yardımcı Teknik Web & Schema Kontrolleri',
      'Kaynak Domain ve Bulunabilirlik Analizleri',
      'Yıllık Kesintisiz AI Yerel Görünürlük Altyapısı',
    ],
  },
  AI_SEARCH_VISIBILITY: {
    id: 'AI_SEARCH_VISIBILITY',
    name: 'AI Arama Görünürlüğü',
    industry: 'CORPORATE',
    price: 5000,
    priceTRY: 5000,
    currency: 'TRY',
    billingPeriod: 'Yıllık',
    storageGb: 0,
    websiteLimit: 0,
    maxSites: 0,
    isWebsiteSubscription: false,
    description: 'ChatGPT Search, Gemini ve modern yapay zeka arama motorlarında markanızın ve işletmenizin doğru, tutarlı web verileriyle bulunabilirliğini artıran teknik SEO ve varlık optimizasyonu.',
    features: [
      'AI Arama Motorları İçin Yapılandırılmış Veri (JSON-LD)',
      'Marka & İşletme Varlık Bilgisi Standardizasyonu',
      'Otomatik Sitemap.xml & Robots.txt Senkronizasyonu',
      'Arama & Grounding Uyumlu İşletme Bilgi Sayfası',
      'Entity Tutarlılık ve Bilgi Bütünlüğü Denetimi',
      'Teknik SEO & Canonical Doğrulama Takibi',
      'Yıllık Kesintisiz AI Görünürlük Altyapısı',
    ],
  },
  TRIAL_3_DAY: {
    id: 'TRIAL_3_DAY',
    name: '3 Günlük Ücretsiz Deneme',
    industry: 'CORPORATE',
    price: 0,
    priceTRY: 0,
    currency: 'TRY',
    billingPeriod: 'Yıllık',
    storageGb: 5,
    websiteLimit: 1,
    maxSites: 1,
    description: '3 gün boyunca sistemi test edebilmeniz için sağlanan 1 web sitesi deneme hakkı.',
    features: [
      '3 Gün Süreli Tam Erişim',
      '1 Adet Web Sitesi Oluşturma',
      'Görsel Editör & Sayfa Yönetimi',
    ],
  },
};

// Production: No seed or mock data. All entities are user-created and persisted in live database.
export const INITIAL_YACHTS_SEED: YachtItem[] = [];
export const INITIAL_ROUTES_SEED: YachtRoute[] = [];
export const INITIAL_MARINAS_SEED: YachtMarina[] = [];
export const INITIAL_BLOG_POSTS_SEED: BlogPost[] = [];

export const STOCK_IMAGES = [
  { name: 'Modern Plazalar & Ofis', url: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1200&auto=format&fit=crop' },
  { name: 'Toplantı & Yönetim', url: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=1200&auto=format&fit=crop' },
  { name: 'Teknoloji & Kodlama', url: 'https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=1200&auto=format&fit=crop' },
  { name: 'Lüks Süperyat Seyir', url: 'https://images.unsplash.com/photo-1569263979104-865ab7cd8d17?q=80&w=1200&auto=format&fit=crop' },
  { name: 'Turkuaz Koylarda Gulet', url: 'https://images.unsplash.com/photo-1540946485063-a40da27545f8?q=80&w=1200&auto=format&fit=crop' },
  { name: 'Marina & Gün Batımı', url: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?q=80&w=1200&auto=format&fit=crop' },
  { name: 'Mimari & Yapı', url: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=1200&auto=format&fit=crop' },
  { name: 'Klinik & Sağlık', url: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?q=80&w=1200&auto=format&fit=crop' },
];
